import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Card,
    CalendarIcon,
    MapMarkerIcon,
    FlowEndIcon,
    Spinner,
    InfoSignIcon,
    Tooltip,
    SmallCrossIcon,
    PeopleIcon,
    PersonIcon,
} from 'evergreen-ui';
import { PoweredBy } from '../../components/index';
import { request, formatAs, history } from 'cademy-shared-web';
import moment from 'moment';
import styles from './BookingSideSummary.module.css';
import { LinkComponent } from '../_ui/A';
import { CourseImage } from '../_ui/Img';
import { PaymentStatusBadge } from './components';
import OnRequestPricingLabel from './components/OnRequestPricingLabel';
import { Environment } from 'services/Environment';
import { Temporal } from 'temporal-polyfill';

let summaryProm = Promise.resolve();

export const BookingSideSummary = ({
    course: { _id: courseRef, type, name, address: courseAddress, slug },
    course,
    occurrence,
    extras,
    bookingOrOrder,
    spaces,
    embedded = false,
    subdomain,
    voucher,
    offersAndAddons = {},
    overrides = {},
    passedUpCostSummary,
    payment_method,
    educator,
}) => {
    const [fetchingSummary, setFetchingSummary] = useState(false);
    const [costSummary, setCostSummary] = useState(null);

    const delivery = useMemo(() => {
        if (course.type === 'on-request' && occurrence) {
            return occurrence.delivery;
        }
        return course.delivery;
    }, [course, occurrence]);

    const showPricing = useMemo(() => {
        if (educator?.feature_flags?.hide_pricing !== true) return true;
    }, [educator]);

    const renderTimes = () => {
        if (course.type === 'on-request') {
            return `Dates agreed on request`;
        }
        // TODO: Make this more DRY
        if (occurrence && Object.keys(occurrence).length > 1) {
            const startDateTime =
                occurrence.is_block && occurrence.children && occurrence.children.length
                    ? Temporal.ZonedDateTime.from(occurrence.children[0].times.start.zoned_datetime)
                    : Temporal.ZonedDateTime.from(occurrence.times.start.zoned_datetime);
            const endDateTime = Temporal.ZonedDateTime.from(occurrence.times.end.zoned_datetime);

            if (startDateTime.toPlainDate().equals(endDateTime.toPlainDate())) {
                return `${startDateTime.toLocaleString(undefined, { day: 'numeric', month: 'numeric', year: '2-digit', hour: 'numeric', minute: 'numeric' })} → ${endDateTime.toLocaleString(undefined, { hour: 'numeric', minute: 'numeric', timeZoneName: 'short' })}`;
            } else {
                return `${startDateTime.toLocaleString(undefined, { day: 'numeric', month: 'numeric', year: '2-digit', hour: 'numeric', minute: 'numeric', timeZoneName: 'short' })} → ${endDateTime.toLocaleString(undefined, { day: 'numeric', month: 'numeric', year: '2-digit', hour: 'numeric', minute: 'numeric', timeZoneName: 'short' })}`;
            }
        } else if (overrides.start_datetime && overrides.end_datetime) {
            if (
                moment
                    .unix(overrides.start_datetime)
                    .isSame(moment.unix(overrides.end_datetime), 'day')
            ) {
                return `${moment.unix(overrides.start_datetime).format('DD/MM/YY, h:mma')} → ${moment.unix(overrides.end_datetime).format(' h:mma ')}`;
            } else {
                return `${moment.unix(overrides.start_datetime).format('DD/MM/YY, h:mma')} → ${moment.unix(overrides.end_datetime).format('DD/MM/YY, h:mma ')}`;
            }
        }
        return 'No date selected yet';
    };

    const totalSpaces = offersAndAddons.offers.reduce(
        (acc, curVal) => acc + curVal.quantity * (curVal.spaces || 1),
        0
    );
    const displayedSpaces = offersAndAddons.offers.length ? totalSpaces : spaces;

    useEffect(() => {
        const fetchSummary = async (lastPayload) => {
            const getOriginStr = () => {
                if (embedded) return 'embed';
                if (subdomain) return 'landing_page';
                return 'marketplace';
            };

            let thing_key, thingRef;
            if (bookingOrOrder) {
                thing_key = bookingOrOrder.items ? 'order_ref' : 'booking_ref';
                thingRef = bookingOrOrder._id;
            } else {
                thing_key = 'course_ref';
                thingRef = courseRef;
            }

            const payload = {
                [thing_key]: thingRef,
                ...offersAndAddons,
                extras,
                voucher_ref: voucher?.voucher_id,
                occurrence_ref: occurrence?._id,
                origin: getOriginStr(),
                payment_method,
            };

            // Ensure that any if nothing has changed, we don't make any new requests
            if (JSON.stringify(lastPayload) === JSON.stringify(payload)) {
                return payload;
            }

            setFetchingSummary(true);
            const { data } = await request.post(`/custom/get-checkout-summary`, payload);
            setCostSummary(data);
            setFetchingSummary(false);
            if (passedUpCostSummary) passedUpCostSummary(data);
            return payload;
        };

        if (bookingOrOrder?._id || offersAndAddons?.offers?.length > 0) {
            // Hacky, but prevents multiple calls overriding each other
            summaryProm = summaryProm.then(fetchSummary).catch((err) => {
                console.error(err);
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bookingOrOrder, offersAndAddons, extras, voucher, courseRef]);

    const displayDatePreview = () => {
        if (type === 'on-demand') return null;

        return (
            <li aria-label="Booking data">
                <CalendarIcon flexShrink={0} />
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'start' }}>
                    {renderTimes()}
                    {Intl.DateTimeFormat().resolvedOptions().timeZone === 'Europe/London' ? null : (
                        <span
                            style={{
                                color: 'var(--text-muted)',
                                fontWeight: '300',
                                display: 'block',
                            }}
                        >
                            Times shown in your local time zone. Organiser time zone is London/GMT
                        </span>
                    )}
                </div>
            </li>
        );
    };

    const displaySpaces = () => {
        if (type === 'on-demand') return null;

        return (
            <li aria-label="Booking spaces">
                {displayedSpaces > 1 ? (
                    <PeopleIcon flexShrink={0} />
                ) : (
                    <PersonIcon flexShrink={0} />
                )}
                {displayedSpaces}
                {displayedSpaces > 1 ? ` spaces` : ' space'}
            </li>
        );
    };

    const displayCademyFees = () => {
        // if we have a bookingOrOrder && fees_absorbed = false
        //display fees from booking
        // else if, cademyFees > 0
        // display fees from cademyFees

        const feeToDisplay = costSummary?.cademy_fees;
        if (!feeToDisplay && !fetchingSummary) return null;
        if (costSummary?.fees_absorbed === true) return null;

        const getPrice = () => formatAs.price(feeToDisplay, course.currency);
        const displayPrice = () =>
            fetchingSummary ? <Spinner size={16} marginRight={5} /> : getPrice();

        return (
            <li className={styles.breakdown_item} aria-label="Booking fees">
                <span style={{ display: 'flex', alignItems: 'center' }}>
                    Service Fees
                    <Tooltip content="Payment processing fees charged by the booking & card processing services to handle the payment securely">
                        <InfoSignIcon size={12} color="muted" marginLeft={5} />
                    </Tooltip>
                </span>
                <span
                    style={{ fontWeight: '700', display: 'flex', alignItems: 'center' }}
                    data-cy="cademy_fees"
                >
                    {displayPrice()}
                </span>
            </li>
        );
    };

    const getCourseUrl = () => {
        const searchParams = history.location.search || '';
        if (embedded) return `/embed/${educator.slug}/${course.slug}${searchParams}`;
        if (subdomain) return `/${course.slug}${searchParams}`;
        return `/${educator.slug}/${course.slug}${searchParams}`;
    };

    const displayDelivery = () => {
        let text = delivery;
        if (delivery === 'on-demand') text += ' / self-paced';

        if (delivery === 'on-request' && overrides.hasOwnProperty('delivery'))
            text = overrides.delivery;

        if (delivery === 'hybrid') text = 'Online & In-person';

        return (
            <li style={{ textTransform: 'capitalize' }} aria-label="Booking delivery type">
                <FlowEndIcon flexShrink={0} /> {text}
            </li>
        );
    };

    const displayLocation = useCallback(() => {
        const classesWithLocations = ['in-person', 'hybrid'];
        if (!occurrence || !classesWithLocations.includes(delivery)) {
            return null;
        }
        if (occurrence.delivery && !classesWithLocations.includes(occurrence.delivery)) {
            return null;
        }
        const itemAddress = occurrence.address ? occurrence.address : courseAddress;
        if (!itemAddress) {
            return null;
        }
        const formattedAddress = Object.values(itemAddress)
            .filter((addressLine) => addressLine !== null)
            .join(', ');
        return (
            <li aria-label="Booking location">
                <MapMarkerIcon flexShrink={0} style={{ alignSelf: 'baseline' }} />{' '}
                {formattedAddress}
            </li>
        );
    }, [courseAddress, delivery, occurrence]);

    const displayCancellationPolicy = () => {
        if (!educator || !educator.cancellation_allowed || type === 'on-demand') return null;

        return (
            <div>
                <SmallCrossIcon flexShrink={0} /> Free cancellation {educator.cancellation_allowed}{' '}
                days prior
            </div>
        );
    };

    const allSummary = costSummary
        ? [
              ...costSummary.summary_items.offerSummary,
              ...costSummary.summary_items.addOnSummary,
              ...costSummary.summary_items.discountSummary,
              ...costSummary.summary_items.voucherSummary,
              ...costSummary.summary_items.extraSummary,
              ...(costSummary.summary_items.vatSummary && bookingOrOrder.currency.iso_code === 'GBP'
                  ? [...costSummary.summary_items.vatSummary]
                  : []),
          ]
        : [];

    return (
        <div className={styles.summary_card_container}>
            <Card className={styles.summary_card} elevation={2} background="white">
                <div className={styles.summary_card_top}>
                    <div>
                        <LinkComponent to={getCourseUrl()}>
                            <h3 style={{ marginTop: '0px' }} className="hover_blue">
                                {name}
                            </h3>
                        </LinkComponent>
                        {embedded || subdomain ? null : (
                            <LinkComponent to={`/${educator.slug}`}>{educator.name}</LinkComponent>
                        )}
                    </div>
                    <CourseImage
                        course={course}
                        className={styles.dated_rows_card_img}
                        size={200}
                    />
                </div>
                <ul className={styles.summary_highlights} aria-label="Key booking information">
                    {displayDatePreview()}
                    {displaySpaces()}
                    {displayDelivery()}
                    {displayLocation()}
                    {displayCancellationPolicy()}
                </ul>

                {allSummary.length && showPricing ? (
                    <div className={styles.costs_breakdown}>
                        <h3>Summary</h3>
                        {allSummary.map(({ text, value }, index) => {
                            return (
                                <div key={`${text}_${index}`} className={styles.breakdown_item}>
                                    <span>{text}</span>
                                    <span style={{ fontWeight: '700' }} data-cy={text}>
                                        {formatAs.price(value, bookingOrOrder.currency.iso_code)}
                                    </span>
                                </div>
                            );
                        })}
                        {!costSummary?.fees_absorbed && !fetchingSummary && displayCademyFees()}
                    </div>
                ) : null}

                {costSummary && showPricing ? (
                    <div className={styles.summary_costs}>
                        <span>Total</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <PaymentStatusBadge bookingOrOrder={bookingOrOrder} />
                            <span data-cy="total">
                                {formatAs.priceShowFree(
                                    costSummary?.total_cost,
                                    bookingOrOrder.currency.iso_code
                                )}
                            </span>
                        </span>
                    </div>
                ) : null}

                <OnRequestPricingLabel course={course} bookingOrOrder={bookingOrOrder} />
            </Card>
            {embedded || subdomain ? (
                <PoweredBy url={`${Environment.MARKETPLACE_URL}/${educator.slug}/${slug}`} />
            ) : null}
        </div>
    );
};
